import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { trackPurchaseCompleted, trackGetStartedClick, trackFormSubmission } from '../utils/analytics';

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userInfo, setUserInfo] = useState({
    email: '',
    name: '',
    company: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const plans = [
    {
      name: 'Free',
      description: 'Perfect for getting started',
      monthlyPrice: 0,
      annualPrice: 0,
      features: [
        'Up to 50 expenses per month',
        'Basic categorization',
        'Web dashboard access',
        'Email support',
        'Basic reporting'
      ],
      limitations: [
        'No AI categorization',
        'No integrations',
        'Limited reports'
      ],
      cta: 'Get Started Free',
      popular: false,
      stripeProductId: null
    },
    {
      name: 'Pro',
      description: 'Best for growing businesses',
      monthlyPrice: 29,
      annualPrice: 290, // 2 months free
      features: [
        'Unlimited expenses',
        'AI-powered categorization',
        'QuickBooks & Zoho integration',
        'Advanced reporting',
        'Receipt scanning (OCR)',
        'Team collaboration',
        'Priority support',
        'Custom categories',
        'Export to Excel/PDF'
      ],
      limitations: [],
      cta: 'Start Pro Trial',
      popular: true,
      stripeProductId: 'prod_pro_123' // Replace with your Stripe product ID
    },
    {
      name: 'Enterprise',
      description: 'For large organizations',
      monthlyPrice: 99,
      annualPrice: 990, // 2 months free
      features: [
        'Everything in Pro',
        'Multi-company support',
        'Advanced analytics',
        'Custom integrations',
        'Dedicated account manager',
        'SSO authentication',
        'API access',
        'White-label options',
        'Custom workflows',
        'SLA guarantee'
      ],
      limitations: [],
      cta: 'Contact Sales',
      popular: false,
      stripeProductId: 'prod_enterprise_123' // Replace with your Stripe product ID
    }
  ];

  const handleSubscribe = (plan) => {
    // Track "Get Started" click on pricing cards
    trackGetStartedClick(`pricing_card_${plan.name.toLowerCase()}`);
    
    // Show modal to collect user info for all plans
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!userInfo.email || !userInfo.name) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      if (selectedPlan.name === 'Free') {
        // Handle free plan signup - save user info and redirect to signup
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userInfo.email,
            name: userInfo.name,
            company: userInfo.company,
            plan: 'free'
          }),
        });

        if (response.ok) {
          // Track free plan signup completion
          trackPurchaseCompleted({
            planName: 'Free',
            price: 0,
            isAnnual: false,
            currency: 'USD'
          });
          
          // Track form submission
          trackFormSubmission('free_plan_signup', {
            plan: 'free',
            email_provided: !!userInfo.email,
            company_provided: !!userInfo.company
          });
          
          // Redirect to success page with user info
          const params = new URLSearchParams({
            plan: 'free',
            email: userInfo.email,
            name: userInfo.name
          });
          window.location.href = `/signup-success?${params.toString()}`;
        } else {
          const error = await response.json();
          alert(error.message || 'Something went wrong. Please try again.');
        }
        return;
      }

      if (selectedPlan.name === 'Enterprise') {
        // Handle enterprise contact - save user info and redirect to contact
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/enterprise-inquiry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userInfo.email,
            name: userInfo.name,
            company: userInfo.company,
            plan: 'enterprise',
            message: 'Interested in Enterprise plan'
          }),
        });

        if (response.ok) {
          // Track enterprise inquiry
          trackFormSubmission('enterprise_inquiry', {
            plan: 'enterprise',
            email_provided: !!userInfo.email,
            company_provided: !!userInfo.company
          });
          
          // Redirect to success page for enterprise inquiry
          const params = new URLSearchParams({
            plan: 'enterprise',
            email: userInfo.email,
            name: userInfo.name,
            company: userInfo.company || ''
          });
          window.location.href = `/signup-success?${params.toString()}`;
        } else {
          const error = await response.json();
          alert(error.message || 'Something went wrong. Please try again.');
        }
        return;
      }

      // Handle Pro plan - contact sales (same as Enterprise)
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/enterprise-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userInfo.email,
          name: userInfo.name,
          company: userInfo.company,
          plan: 'pro',
          message: 'Interested in Pro plan'
        }),
      });

      if (response.ok) {
        // Track pro inquiry
        trackFormSubmission('pro_inquiry', {
          plan: 'pro',
          email_provided: !!userInfo.email,
          company_provided: !!userInfo.company
        });
        
        // Redirect to success page for pro inquiry
        const params = new URLSearchParams({
          plan: 'pro',
          email: userInfo.email,
          name: userInfo.name,
          company: userInfo.company || ''
        });
        window.location.href = `/signup-success?${params.toString()}`;
      } else {
        const error = await response.json();
        alert(error.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
    setUserInfo({ email: '', name: '', company: '' });
  };

  return (
    <div className="pt-16 min-h-screen bg-gray-900">
      {/* Header */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Choose the perfect plan for your business. Start free and upgrade as you grow.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-12">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-white' : 'text-gray-400'}`}>
                Monthly
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                  isAnnual ? 'bg-primary-600' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isAnnual ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-white' : 'text-gray-400'}`}>
                Annual
              </span>
              {isAnnual && (
                <span className="bg-secondary-600 text-white text-xs font-medium px-2.5 py-0.5 rounded-full">
                  Save 17%
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`relative bg-gray-800 rounded-2xl shadow-lg border-2 p-8 ${
                  plan.popular ? 'border-primary-500 scale-105' : 'border-gray-700'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-300 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">
                      ${isAnnual ? Math.floor(plan.annualPrice / 12) : plan.monthlyPrice}
                    </span>
                    <span className="text-gray-300">/month</span>
                    {isAnnual && plan.annualPrice > 0 && (
                      <div className="text-sm text-gray-400 mt-1">
                        Billed annually (${plan.annualPrice}/year)
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleSubscribe(plan)}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
                      plan.popular
                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-3">Features included:</h4>
                    <ul className="space-y-2">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <svg className="w-5 h-5 text-secondary-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {plan.limitations.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-white mb-3">Limitations:</h4>
                      <ul className="space-y-2">
                        {plan.limitations.map((limitation, limitationIndex) => (
                          <li key={limitationIndex} className="flex items-start">
                            <svg className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-400 text-sm">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* FAQ */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: "Can I change my plan at any time?",
                answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated and reflected in your next billing cycle."
              },
              {
                question: "Is there a free trial for paid plans?",
                answer: "Yes, we offer a 14-day free trial for both Pro and Enterprise plans. No credit card required to start."
              },
              {
                question: "What integrations do you support?",
                answer: "We currently support QuickBooks Online, Zoho Books, and Xero. We're constantly adding new integrations based on customer feedback."
              },
              {
                question: "How secure is my financial data?",
                answer: "We use bank-level 256-bit SSL encryption and are SOC 2 Type II compliant. Your data is stored securely and never shared with third parties."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-white mb-3">{faq.question}</h3>
                <p className="text-gray-300">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Information Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-8 border border-gray-700"
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">
                Get Started with {selectedPlan?.name}
              </h3>
              <p className="text-gray-300">
                {selectedPlan?.name === 'Free' 
                  ? 'Just a few details to create your free account'
                  : selectedPlan?.name === 'Enterprise'
                  ? 'Tell us about your needs and we\'ll get in touch'
                  : 'Just a few details to get you started with your subscription'
                }
              </p>
            </div>

            <form onSubmit={handleCheckout} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={userInfo.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={userInfo.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={userInfo.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Your Company Inc."
                />
              </div>

              <div className="bg-gray-700 rounded-lg p-4 mt-6">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-white">
                    {selectedPlan?.name} Plan
                  </span>
                  {selectedPlan?.name === 'Enterprise' ? (
                    <span className="text-lg font-bold text-primary-400">
                      Custom Pricing
                    </span>
                  ) : (
                    <span className="text-2xl font-bold text-primary-400">
                      ${isAnnual ? selectedPlan?.annualPrice : selectedPlan?.monthlyPrice}
                      {selectedPlan?.monthlyPrice > 0 && (
                        <span className="text-sm font-normal text-gray-400">
                          /{isAnnual ? 'year' : 'month'}
                        </span>
                      )}
                      {selectedPlan?.monthlyPrice === 0 && (
                        <span className="text-sm font-normal text-gray-400">
                          /forever
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {isAnnual && selectedPlan?.monthlyPrice > 0 && (
                  <p className="text-sm text-green-400 mt-1">
                    Save ${(selectedPlan.monthlyPrice * 12) - selectedPlan.annualPrice} per year!
                  </p>
                )}
              </div>

              <div className="flex space-x-3 mt-8">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    selectedPlan?.name === 'Free' 
                      ? 'Create Free Account'
                      : selectedPlan?.name === 'Enterprise'
                      ? 'Contact Sales Team'
                      : 'Continue to Checkout'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Pricing;